import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import type { SAIntegration } from '@/mocks/saIntegrations';

const statusConfig: Record<SAIntegration['status'], { dot: string; bg: string; text: string; label: string }> = {
  connected:    { dot: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Conectada' },
  disconnected: { dot: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-500', label: 'Desconectada' },
  error:        { dot: 'bg-amber-500 animate-pulse', bg: 'bg-amber-50', text: 'text-amber-600', label: 'Con errores' },
  pending:      { dot: 'bg-secondary-500', bg: 'bg-secondary-50', text: 'text-secondary-600', label: 'Pendiente' },
};

const categoryLabels: Record<SAIntegration['category'], string> = {
  database: 'Base de Datos',
  payments: 'Pagos',
  streaming: 'Streaming',
  email: 'Email',
  infrastructure: 'Infraestructura',
  maps: 'Mapas',
  notifications: 'Notificaciones',
  social: 'Redes Sociales',
  ai: 'Inteligencia Artificial',
};

export default function IntegrationCard({ integration, index }: { integration: SAIntegration; index: number }) {
  const navigate = useNavigate();
  const sc = statusConfig[integration.status];

  const handleTest = async () => {
    const isOk = integration.status === 'connected';
    await Swal.fire({
      icon: isOk ? 'success' : 'error',
      title: isOk ? 'Test exitoso' : 'Test fallido',
      html: `<div style="text-align:center;font-size:13px">
        <p style="color:#6b7280;margin-bottom:6px"><strong>${integration.name}</strong></p>
        <p style="color:#9ca3af;font-size:11px">${isOk ? `Respuesta en ${integration.lastSync}` : integration.lastError || 'Error de conexión'}</p>
      </div>`,
      confirmButtonText: 'Cerrar',
      confirmButtonColor: isOk ? '#10b981' : '#ef4444',
      customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap' },
      buttonsStyling: false,
    });
  };

  return (
    <div
      className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5 flex flex-col gap-3 transition-colors hover:border-background-300/70"
      style={{ animation: `fadeInUp 0.3s ease-out forwards`, animationDelay: `${index * 50}ms`, opacity: 0 }}
    >
      {/* Header: icon + name + status */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl ${sc.bg} flex items-center justify-center shrink-0`}>
            <i className={`${integration.icon} ${sc.text} text-base`}></i>
          </div>
          <div className="min-w-0">
            <p className="text-foreground-950 text-sm font-semibold truncate">{integration.name}</p>
            <p className="text-foreground-400 text-[10px]">{categoryLabels[integration.category]}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap shrink-0 ${sc.bg} ${sc.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
          {sc.label}
        </span>
      </div>

      {/* Info rows */}
      <div className="grid grid-cols-2 gap-2 text-[10px]">
        <div>
          <span className="text-foreground-400">Versión</span>
          <p className="text-foreground-700 font-medium">{integration.version}</p>
        </div>
        <div>
          <span className="text-foreground-400">Última sincro</span>
          <p className="text-foreground-700 font-medium">{integration.lastSync}</p>
        </div>
      </div>

      {integration.lastError && (
        <div className="bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5">
          <p className="text-red-500 text-[9px] font-medium leading-tight">
            <i className="ri-error-warning-line mr-1"></i>
            {integration.lastError}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleTest}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition-colors bg-background-100 text-foreground-600 hover:bg-background-200 whitespace-nowrap"
        >
          <i className="ri-flashlight-line text-[10px]"></i>
          Test
        </button>
        <button
          onClick={() => navigate(integration.configRoute)}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition-colors bg-primary-50 text-primary-600 hover:bg-primary-100 whitespace-nowrap"
        >
          <i className="ri-settings-3-line text-[10px]"></i>
          Config
        </button>
        <button
          onClick={() => navigate(integration.logsRoute)}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition-colors bg-secondary-50 text-secondary-600 hover:bg-secondary-100 whitespace-nowrap"
        >
          <i className="ri-file-list-3-line text-[10px]"></i>
          Logs
        </button>
      </div>
    </div>
  );
}